import React, { useState, useEffect } from 'react';
// FIX: Replaced missing storageService with apiService
import { apiService } from '../services/apiService';
// FIX: Replaced missing storage.interface with types.ts, add ALL_EXPORTABLE_COLUMNS
import { EXPORT_COLUMNS_PREF_KEY, getDefaultExportColumnSettings, ExportColumnSetting, ALL_EXPORTABLE_COLUMNS } from '../types';
import { Spinner } from './Spinner';
import { useAuth } from '../hooks/useAuth';

export interface ExportableColumn {
    key: string;
    label: string;
}

interface ExportModalProps {
  onClose: () => void;
  onExport: (selectedColumns: ExportableColumn[]) => void;
}

export const ExportModal: React.FC<ExportModalProps> = ({ onClose, onExport }) => {
  const [availableColumns, setAvailableColumns] = useState<ExportColumnSetting[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const loadSettings = async () => {
        if (!user) return;
        setIsLoading(true);

        const savedSettings = await apiService.getPreference<ExportColumnSetting[]>(EXPORT_COLUMNS_PREF_KEY, null);
        let finalSettings: ExportColumnSetting[];

        if (savedSettings) {
            const savedSettingsMap = new Map(savedSettings.map(s => [s.key, s]));
            
            finalSettings = ALL_EXPORTABLE_COLUMNS.map(col => {
                const saved = savedSettingsMap.get(col.key);
                if (saved) {
                    return saved;
                }
                // Nový sloupec, který není v uložených preferencích
                return {
                    ...col,
                    isAvailable: true,
                    isDefault: false,
                };
            });
        } else {
            // Žádné uložené nastavení, použijí se výchozí
            finalSettings = getDefaultExportColumnSettings();
        }

        const visibleColumns = finalSettings.filter(s => s.isAvailable);
        const defaultSelected = visibleColumns.filter(s => s.isDefault).map(s => s.key);
        
        setAvailableColumns(visibleColumns);
        setSelectedKeys(defaultSelected);
        setIsLoading(false);
    };
    loadSettings();
  }, [user]);
  
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            onClose();
        } else if (event.key === 'Enter') {
            event.preventDefault();
            const selectedColumns = availableColumns.filter(c => selectedKeys.includes(c.key));
            if (selectedColumns.length > 0) {
                onExport(selectedColumns);
            }
        }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
        document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, onExport, availableColumns, selectedKeys]);


  const handleCheckboxChange = (key: string) => {
    setSelectedKeys(prev => 
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };
  
  const handleSelectAll = () => {
    setSelectedKeys(availableColumns.map(c => c.key));
  };
  
  const handleDeselectAll = () => {
    setSelectedKeys([]);
  };

  const handleExportClick = () => {
    const selectedColumns = availableColumns.filter(c => selectedKeys.includes(c.key));
    if (selectedColumns.length > 0) {
        onExport(selectedColumns);
    } else {
        alert('Vyberte prosím alespoň jeden sloupec pro export.');
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center" 
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="export-modal-title"
    >
      <div 
        className="bg-white rounded-lg shadow-xl p-6 m-4 max-w-lg w-full max-h-[80vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center pb-3 border-b">
          <h2 id="export-modal-title" className="text-xl font-bold text-gray-800">Exportovat do CSV</h2>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Zavřít"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {isLoading ? (
            <div className="flex-grow flex items-center justify-center">
                <Spinner text="Načítám nastavení exportu..." />
            </div>
        ) : (
            <>
                <div className="my-4">
                    <p className="text-gray-600 mb-4">Vyberte sloupce, které chcete zahrnout do exportu.</p>
                    <div className="flex items-center gap-4 mb-4">
                        <button onClick={handleSelectAll} className="text-sm text-blue-600 hover:underline">Vybrat vše</button>
                        <button onClick={handleDeselectAll} className="text-sm text-blue-600 hover:underline">Odebrat vše</button>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 max-h-60 overflow-y-auto border p-3 rounded-md">
                        {availableColumns.map(col => (
                            <label key={col.key} className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    checked={selectedKeys.includes(col.key)}
                                    onChange={() => handleCheckboxChange(col.key)}
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className="text-gray-700">{col.label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="mt-auto pt-4 border-t flex justify-end gap-3">
                  <button 
                    onClick={onClose} 
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                  >
                    Zrušit
                  </button>
                  <button 
                    onClick={handleExportClick}
                    className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors disabled:bg-green-300"
                    disabled={selectedKeys.length === 0}
                  >
                    Exportovat
                  </button>
                </div>
            </>
        )}
      </div>
    </div>
  );
};